import { Grid, Tooltip } from "@mui/material";
import { FC, useEffect, useState } from "react";
import GeneralListItem from "@/components/general/generaListItem";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useTranslation } from "react-i18next";
import Modal from "@/components/modal";
import FileUploadInput from "@/components/forms/inputs/upload";
import EmailIcon from "@mui/icons-material/Email";
import { api } from "@/helper/api";
import GenericNotification from "@/components/notification/genericNotification";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import styles from "@/components/appointmentDashboard/appointmentTreeItemUploadButton.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";

interface Props {
  useCase: "formOverview" | "documentWidget" | "button";
  subject?: any;
  buttonCaseTitle?: any;
  buttonCaseOnClick?: () => void;
  buttonCaseDisabled?: boolean;
  margin?: string;
  wrapperStyle?: any;
  buttonCaseIcon?: any;
  handleTriggerReload?: () => void;
  appointmentId?: string;
  renderDocumentCaseAsButton?: boolean;
  renderDocumentCaseAsButtonDisabled?: boolean;
  renderDocumentCaseAsButtonTitle?: string;
  renderDocumentCaseAsButtonWithTooltip?: boolean;
  renderDocumentCaseAsButtonTooltipText?: string;
  buttonVariant?: "contained" | "outlined" | "icon";
}

const AppointmentTreeItemUploadButton: FC<Props> = ({
  useCase,
  subject,
  buttonCaseTitle,
  buttonCaseOnClick,
  buttonCaseDisabled,
  margin,
  wrapperStyle,
  buttonCaseIcon,
  handleTriggerReload,
  appointmentId,
  renderDocumentCaseAsButton,
  renderDocumentCaseAsButtonDisabled,
  renderDocumentCaseAsButtonTitle,
  renderDocumentCaseAsButtonWithTooltip,
  renderDocumentCaseAsButtonTooltipText,
  buttonVariant,
}) => {
  const { t } = useTranslation();
  const [currentSubject, setCurrentSubject] = useState<any>();
  const [modalUploadOpen, setModalUploadOpen] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const { handleError, errorModal } = useErrorModal();

  const { showConfirmationModalAsync } = useConfirmationModal();

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };
  useEffect(() => {
    if (subject) {
      setCurrentSubject(subject);
    }
  }, []);
  let buttonTitle = "";

  //ohne function
  if (!currentSubject?.is_registered) {
    buttonTitle = t("subject_not_registered_warning");
  }

  if (currentSubject?.is_analog && !currentSubject?.is_registered) {
    buttonTitle = t("evk_missing_please_upload");
  }

  const handleSendRegistrationEmail = async (
    skipQuestion?: false | undefined,
  ) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("send_registration_email_question", {
          email: currentSubject?.email,
        }),
      );
      if (!isConfirmed) return;
    }
    const additionalRouteParts: any = {};
    additionalRouteParts["send_user_registration_email"] = "/";

    api.genericApiRequest({
      entity: "subject",
      method: "post",
      submitData: {},
      successHandler: () => {
        setNotificationMessage(t("successfully_sent"));
        setNotificationVariant("success");
        setSuccessForNotification(true);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: currentSubject?.id,
      additionalRouteParts: additionalRouteParts,
    });
  };

  const FormOverviewCase = () => {
    return (
      <Grid item container style={wrapperStyle}>
        {!currentSubject?.is_registered && !currentSubject?.is_analog && (
          <Grid item>
            <GeneralListItem
              allowHover={true}
              onClickElement="wrapper"
              hideButton={true}
              margin={margin}
              wrapperClassName={styles.generalListItemWrapper__sendEmailBtn}
              onClick={() => handleSendRegistrationEmail(false)}
            >
              <Tooltip title={t("send_registration_email")} placement="top-end">
                <div className={styles.buttonContainer}>
                  <h6 className={styles.noMargin}>
                    {t("send_registration_email")}
                  </h6>
                  <EmailIcon />
                </div>
              </Tooltip>
            </GeneralListItem>
          </Grid>
        )}
        <Grid item>
          {!currentSubject?.is_registered && (
            <GeneralListItem
              allowHover={true}
              onClickElement="wrapper"
              hideButton={true}
              margin={margin}
              wrapperClassName={styles.generalListItemWrapper__uploadBtn}
              onClick={() => setModalUploadOpen(true)}
            >
              <Tooltip title={t("upload")} placement="top-end">
                <div className={styles.buttonContainer}>
                  <h6 className={styles.noMargin}>{buttonTitle}</h6>
                  <FileUploadIcon />
                </div>
              </Tooltip>
            </GeneralListItem>
          )}
        </Grid>
      </Grid>
    );
  };

  const DocumentWidgetCase = () => {
    return (
      <>
        {renderDocumentCaseAsButton ? (
          (() => {
            const btn = (
              <span>
                <GenericButton
                  variant={buttonVariant ?? "contained"}
                  onClick={() => setModalUploadOpen(true)}
                  startIcon={<FileUploadIcon />}
                  disabled={renderDocumentCaseAsButtonDisabled}
                >
                  {renderDocumentCaseAsButtonTitle ?? t("upload")}
                </GenericButton>
              </span>
            );
            if (renderDocumentCaseAsButtonWithTooltip) {
              return (
                <Tooltip
                  placement="top"
                  title={
                    renderDocumentCaseAsButtonTooltipText ??
                    t("documents_of_appointment_upload")
                  }
                >
                  {btn}
                </Tooltip>
              );
            }
            return btn;
          })()
        ) : (
          <Tooltip placement="top" title={t("documents_of_appointment_upload")}>
            <GenericButton
              className={styles.uploadButton}
              variant="icon"
              onClick={() => setModalUploadOpen(true)}
            >
              <FileUploadIcon />
            </GenericButton>
          </Tooltip>
        )}
      </>
    );
  };

  const ButtonCase = () => {
    return (
      <GeneralListItem
        allowHover={true}
        margin={margin}
        onClickElement="wrapper"
        hideButton={true}
        wrapperClassName={
          !buttonCaseDisabled
            ? styles.generalListItemWrapper__BtnCase
            : styles.generalListItemWrapper__BtnCase_disabled
        }
        onClick={buttonCaseOnClick}
        isDisabled={buttonCaseDisabled}
      >
        <div className={styles.buttonContainer}>
          <h6 className={styles.noMargin}>{buttonCaseTitle}</h6>
          {buttonCaseIcon}
        </div>
      </GeneralListItem>
    );
  };

  const renderElement = () => {
    switch (useCase) {
      case "formOverview":
        return <FormOverviewCase />;
      case "documentWidget":
        return <DocumentWidgetCase />;
      case "button":
        return <ButtonCase />;
      default:
        return <p>Unknown status</p>;
    }
  };

  return (
    <div>
      {renderElement()}

      <Modal
        open={modalUploadOpen}
        onClose={() => setModalUploadOpen(false)}
        title={t("upload_file")}
        hideDialogActions={true}
        disableCancelButton={uploadInProgress}
        maxWidth="40rem"
      >
        <FileUploadInput
          multiple={true}
          appointmentId={appointmentId}
          subjectId={currentSubject?.id}
          folderId={"00000026-1111-0000-0000-000000000000"}
          name="file"
          evkUpload={useCase === "documentWidget" ? false : true}
          documentsForAppointmentUpload={
            useCase === "documentWidget" ? true : false
          }
          onUploadStateChange={setUploadInProgress}
          onCancel={() => {
            setModalUploadOpen(false);
          }}
          handleUploadSuccess={(modalOpen: boolean) => {
            if (modalOpen) {
              setModalUploadOpen(false);
              handleTriggerReload?.();
            }
          }}
        />
      </Modal>
      {errorModal}
      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </div>
  );
};

export default AppointmentTreeItemUploadButton;
